import React, { useState, useEffect, useRef } from "react";
import { FcGoogle } from "react-icons/fc";
import { Star, ThumbsUp, CheckCircle2, MessageSquare, Filter, X, Search, Award, Calendar, Sparkles, Settings, Key, Eye, EyeOff, Save, ShieldAlert, Check, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../../axios";
import { getS3URL } from "../../../_helpers";


function starLabel(n) {
  return ["", "Poor", "Fair", "Good", "Very Good", "Excellent"][n] || "";
}

function getLocalSuggestions(stars, courseName, courseTopics) {
  const topicsList = courseTopics
    ? courseTopics.split(",").map((t) => t.trim()).filter(Boolean)
    : [];
  const t1 = topicsList[0] || "key concepts";
  const t2 = topicsList[1] || topicsList[0] || "practical exercises";
  const t3 = topicsList[2] || topicsList[0] || "real-world projects";

  switch (stars) {
    case 5:
      return [
        `Outstanding course! It exceeded all my expectations for learning ${courseName}.`,
        `The sections on ${t1} and ${t2} were extremely clear and easy to follow. The projects helped solidify my understanding.`,
        `I highly recommend this course to anyone. The instructor explains complicated concepts in a very simple way.`,
        `Absolutely brilliant! The material covering ${t1} is top-notch, and the hands-on assignments are highly practical. This is definitely the best course on ${courseName} that I have taken.`,
        `Amazing learning experience! The curriculum is incredibly well structured and covers ${t3} in a very engaging way.`,
        `The hands-on lab exercises and real-world case studies on ${t1} gave me the confidence to apply these skills in my job.`,
        `Perfect balance of theory and practice. The tutor's expertise in ${courseName} shines through in every lesson.`,
        `If you want to master ${courseName}, this is the course to take. The support community and tutor response time are superb.`
      ];
    case 4:
      return [
        `Very good course, I learned a lot of useful skills.`,
        `The explanations of ${t1} were very solid and easy to understand. The pacing was just right for a beginner.`,
        `Really liked the hands-on exercises, especially the ones about ${t2}. It was a great learning experience.`,
        `A well-structured guide to ${courseName}. It covers almost everything you need, including ${t1} and ${t2}, though I wish some sections went a bit deeper. Still, it's highly recommended!`,
        `Great course with practical projects! The modules on ${t3} were detailed and easy to absorb.`,
        `Highly educational and covers a wide range of topics. A few quizzes had minor bugs, but overall it was a great experience.`,
        `Clear lectures, good pace, and excellent code walk-throughs for ${t1}. Definitely worth the enrollment.`,
        `Strong content and useful assignments. It has helped me upskill quickly in ${courseName}.`
      ];
    case 3:
      return [
        `Decent course, but it could be improved in some areas.`,
        `It covers the basics of ${t1} well, but the examples felt a bit too simple. I was hoping for more advanced content.`,
        `The pacing was a bit slow in the sections about ${t2}. It is a reasonable introduction, but not very comprehensive.`,
        `A satisfactory starting point for learning ${courseName}. The instructor knows the topic, but the videos could be more engaging. It is good for getting a basic understanding of ${t1}, but you will need external resources for deeper knowledge.`,
        `The theory is good, but I would have preferred more focus on ${t3} and real-world implementation.`,
        `Average course. The videos are a bit long and could be more concise.`,
        `Good overview of ${courseName}, but some explanations of ${t1} felt rushed.`,
        `An okay introduction, but lacks the depth required for professional projects.`
      ];
    case 2:
      return [
        `A bit disappointing. It did not live up to my expectations.`,
        `The instructor moves too fast through ${t1} without explaining the background. It is quite hard for beginners to keep up.`,
        `A lot of the content felt outdated, and the coding exercises for ${t2} had unresolved errors. The support forum was not very active.`,
        `Unfortunately, this course was not very helpful for me. While it technically covers ${courseName}, the explanation of ${t1} is very shallow. I ended up spending more time on Google than watching the actual lectures.`,
        `The audio and video quality were quite poor, making it hard to follow the lectures on ${t2}.`,
        `Too much theory and not enough practical guidance on ${t3}. It was hard to stay motivated.`,
        `I found the course structure confusing. It jumps between basic and advanced topics without logical transition.`,
        `Many lessons felt like reading documentation slides instead of showing actual coding or implementation.`
      ];
    case 1:
      return [
        `Very poor quality. I do not recommend this course at all.`,
        `The structure is confusing and the examples on ${t1} simply do not work. It feels very rushed and unorganized.`,
        `The explanations are extremely dry and hard to follow. The instructor just reads off the slides without any real explanation.`,
        `This course on ${courseName} was a complete waste of time. The material is completely outdated, and key topics like ${t1} are barely explained. There are much better free tutorials available elsewhere.`,
        `I could not complete the course because the lab exercises on ${t2} were broken and no support was available.`,
        `Very low quality. The explanations of ${t3} were incorrect and misleading.`,
        `Save your money. The teaching style is frustrating and the course lacks any updates.`,
        `Terrible course. It is outdated, lacks structured walkthroughs, and is not worth the price.`
      ];
    default:
      return [];
  }
}


function ReviewNode({ review, onReplySubmit, handleHelpfulClick, helpfulVotes, level = 0 }) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    try {
      setSubmitting(true);
      setErrorMsg("");
      await onReplySubmit(review.id, replyText);
      setReplyText("");
      setShowReplyForm(false);
      setShowReplies(true);
    } catch (err) {
      setErrorMsg(err.message || "Failed to submit reply.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderRoleBadge = (role) => {
    if (role === 1 || role === "1") {
      return (
        <span className="ml-2 inline-flex items-center gap-0.5 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider bg-violet-50 border border-violet-200 text-violet-700 rounded-md shadow-2xs">
          <Award className="w-2.5 h-2.5" /> Admin
        </span>
      );
    }
    if (role === 4 || role === "4") {
      return (
        <span className="ml-2 inline-flex items-center gap-0.5 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider bg-emerald-50 border border-emerald-200 text-emerald-750 rounded-md shadow-2xs">
          <Award className="w-2.5 h-2.5" /> Instructor
        </span>
      );
    }
    return null;
  };

  const getGradient = (name = "") => {
    const avatarGradients = [
      "from-violet-500 to-purple-600",
      "from-blue-500 to-[#264b82]",
      "from-emerald-500 to-teal-600",
      "from-orange-500 to-amber-600",
      "from-pink-500 to-rose-600",
      "from-cyan-500 to-sky-600",
    ];
    return avatarGradients[name.charCodeAt(0) % avatarGradients.length];
  };

  const isLongText = review.text && review.text.length > 300;
  const displayText = isExpanded ? review.text : (review.text ? review.text.slice(0, 300) + (isLongText ? "..." : "") : "");

  return (
    <div className={`bg-white p-5 sm:p-6 rounded-3xl border border-slate-100 shadow-xs space-y-3.5 transition-all duration-300 relative ${
      level > 0 
        ? "ml-6 sm:ml-12 pl-4 sm:pl-6 border-l-2 border-l-indigo-150 bg-slate-50/20" 
        : "hover:shadow-md hover:-translate-y-0.5"
    }`}>
      {level > 0 && (
        <div className="absolute top-8 left-0 w-3 sm:w-5 h-[1px] bg-indigo-150" />
      )}
      
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          {review.profile_photo_url ? (
            <img
              src={review.profile_photo_url}
              alt={review.author_name}
              className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm ring-1 ring-slate-100"
            />
          ) : (
            <div className={`w-10 h-10 bg-gradient-to-br ${getGradient(review.author_name)} text-white rounded-full flex items-center justify-center font-bold text-sm uppercase shadow-sm border border-white`}>
              {review.author_name.charAt(0)}
            </div>
          )}
          <div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-base font-bold text-slate-800">{review.author_name}</span>
              {renderRoleBadge(review.userRole)}
            </div>
            {review.course_name && level === 0 && (
              <div className="text-[11px] text-indigo-600 font-bold uppercase tracking-wider mt-0.5">
                Course: {review.course_name}
              </div>
            )}
          </div>
        </div>
        
        {review.verified && level === 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 bg-emerald-50/70 border border-emerald-100/50 rounded-full px-2.5 py-0.5 shadow-3xs">
            <CheckCircle2 className="w-3 h-3 fill-emerald-100 text-emerald-600" />
            Verified Reviewer
          </span>
        )}
      </div>

      <div className="space-y-2">
        {level === 0 && (
          <div className="flex text-amber-400">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`w-4 h-4 ${i < review.rating ? "fill-amber-400 text-amber-400" : "text-slate-100"}`}
              />
            ))}
          </div>
        )}
        <div>
          <p className="text-base text-slate-600 leading-relaxed text-justify whitespace-pre-line">
            {displayText}
          </p>
          {isLongText && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sm font-bold text-indigo-600 hover:text-indigo-800 transition-colors mt-1 focus:outline-none cursor-pointer"
            >
              {isExpanded ? "Show Less" : "Read More"}
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 pt-1 border-t border-slate-50/50">
        <div className="flex items-center gap-3">
          {level === 0 && (
            <button
              onClick={() => handleHelpfulClick(review.id)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold transition-all border shadow-3xs cursor-pointer ${
                helpfulVotes[review.id]
                  ? "bg-indigo-50 border-indigo-200 text-indigo-600 scale-[1.03]"
                  : "bg-white border-slate-200 text-slate-500 hover:border-slate-350 hover:bg-slate-50 hover:text-slate-700 active:scale-95"
              }`}
            >
              <ThumbsUp className="w-3 h-3" />
              Helpful ({review.helpful_count})
            </button>
          )}
          
          {localStorage.getItem("token") && (
            <button
              onClick={() => setShowReplyForm(!showReplyForm)}
              className="text-xs font-bold text-slate-500 hover:text-slate-800 hover:bg-slate-50 px-2 py-1 rounded-lg transition-all flex items-center gap-1 cursor-pointer"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              {showReplyForm ? "Cancel Reply" : "Reply"}
            </button>
          )}
        </div>
      </div>

      {showReplyForm && (
        <form onSubmit={handleReplySubmit} className="mt-3 space-y-2 bg-slate-50/50 p-3.5 rounded-2xl border border-slate-100">
          {errorMsg && (
            <div className="p-2.5 bg-red-50 text-red-700 text-xs font-bold rounded-lg border border-red-100">
              {errorMsg}
            </div>
          )}
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder={`Reply to ${review.author_name}...`}
            rows="2"
            className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition-all resize-none shadow-3xs"
            required
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowReplyForm(false)}
              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-xs font-bold transition-all cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-1.5 bg-slate-900 hover:bg-black text-white rounded-lg text-xs font-bold shadow-xs transition-all cursor-pointer"
            >
              {submitting ? "Replying..." : "Submit Reply"}
            </button>
          </div>
        </form>
      )}

      {review.replies && review.replies.length > 0 && (
        <div className="mt-2.5">
          <button
            onClick={() => setShowReplies(!showReplies)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50/40 hover:bg-indigo-50 text-indigo-600 hover:text-indigo-800 rounded-xl text-xs font-bold border border-indigo-100/40 transition-colors cursor-pointer"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            {showReplies ? "Hide replies" : `Show replies (${review.replies.length})`}
          </button>

          {showReplies && (
            <div className="mt-4 space-y-4">
              {review.replies.map((childReview, idx) => (
                <ReviewNode
                  key={childReview.id || idx}
                  review={childReview}
                  onReplySubmit={onReplySubmit}
                  handleHelpfulClick={handleHelpfulClick}
                  helpfulVotes={helpfulVotes}
                  level={level + 1}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Reviews({ courseId: propCourseId, reviews: propReviews }) {
  const { user } = useSelector((state) => state.users || {});
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [course, setCourse] = useState(location.state?.course || null);
  const [dbReviews, setDbReviews] = useState(propReviews || []);
  const [loadingReviews, setLoadingReviews] = useState(!propReviews);
  const [googleReviews, setGoogleReviews] = useState([]);
  const [selectedStar, setSelectedStar] = useState("all");
  const [helpfulVotes, setHelpfulVotes] = useState({});
  const [testimonialReviews, setTestimonialReviews] = useState([]);
  const [userReview, setUserReview] = useState(course?.userReview || null);
  const [coursesList, setCoursesList] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [isPurchased, setIsPurchased] = useState(false);
  const [isCoursePurchased, setIsCoursePurchased] = useState(false);
  const [checkingPurchase, setCheckingPurchase] = useState(false);
  
  // Custom states for search and sort
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("helpful");
  const [hoveredRating, setHoveredRating] = useState(0);

  // Form State for Write Review Modal
  const [showWriteModal, setShowWriteModal] = useState(false);
  const [newRating, setNewRating] = useState(5);
  const [newComment, setNewComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [submitSuccessMsg, setSubmitSuccessMsg] = useState("");
  const [submitErrorMsg, setSubmitErrorMsg] = useState("");

  const getRatingLabel = (val) => {
    switch (val) {
      case 5: return "5 - Excellent, loved it!";
      case 4: return "4 - Very Good, highly recommended";
      case 3: return "3 - Good, standard experience";
      case 2: return "2 - Fair, could be improved";
      case 1: return "1 - Poor, did not meet expectations";
      default: return "Select your rating";
    }
  };

  useEffect(() => {
    if (showWriteModal) {
      if (userReview) {
        setNewRating(Number(userReview.rating || 5));
        setNewComment(userReview.comment || "");
      } else {
        setNewRating(5);
        setNewComment("");
      }
    }
  }, [showWriteModal, userReview]);

  const courseId = propCourseId || course?.ID;
  const courseName = course?.CourseName;

  // --- AI Suggestions state and logic ---
  const [apiKey, setApiKey] = useState(() => {
    const saved = localStorage.getItem("anthropic_api_key");
    if (saved) return saved;
    const envKey = process.env.REACT_APP_ANTHROPIC_KEY;
    return envKey && !envKey.includes("YOUR_ACTUAL_KEY_HERE") ? envKey : "";
  });
  const [showSettings, setShowSettings] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState(apiKey);
  const [keySaved, setKeySaved] = useState(false);
  const [showKey, setShowKey] = useState(false);

  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState("");
  const [showSuggestionsList, setShowSuggestionsList] = useState(false);

  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);

  const targetCourseId = courseId || selectedCourseId;
  const activeCourse = course || coursesList.find(c => String(c.ID) === String(targetCourseId));
  const activeCourseName = activeCourse?.CourseName || courseName || "this course";
  
  // Extract topics from courseOutDescription if available
  const activeCourseTopics = React.useMemo(() => {
    if (!activeCourse?.courseOutDescription) return "";
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(activeCourse.courseOutDescription, "text/html");
      const items = Array.from(doc.querySelectorAll("li"))
        .map(li => li.textContent.trim())
        .filter(Boolean);
      return items.slice(0, 5).join(", ");
    } catch (e) {
      return "";
    }
  }, [activeCourse]);

  const fetchSuggestions = async (stars, partial) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    
    setLoadingSuggestions(true);
    setSuggestionsError("");

    if (!apiKey) {
      try {
        // Simulate a small loading delay for a premium user experience
        await new Promise((resolve, reject) => {
          const timer = setTimeout(resolve, 350);
          abortControllerRef.current.signal.addEventListener("abort", () => {
            clearTimeout(timer);
            reject(new DOMException("Aborted", "AbortError"));
          });
        });
        const localSugs = getLocalSuggestions(stars, activeCourseName, activeCourseTopics);
        setSuggestions(localSugs);
      } catch (e) {
        // If aborted, do nothing
      } finally {
        setLoadingSuggestions(false);
      }
      return;
    }

    const prompt = `You are a helpful writing assistant for course reviews. The user is writing a ${stars}-star review for a course called "${activeCourseName}".
${activeCourseTopics ? `Course topics: ${activeCourseTopics}.` : ""}
${partial ? `The user has started writing: "${partial}"` : "The user hasn't typed anything yet."}

Generate exactly 4 short, natural, distinct review sentence suggestions that:
- Match the ${stars}-star sentiment (${starLabel(stars)})
- Are realistic and specific to the course name and topics
- Continue or complement what the user has written (if anything)
- Vary in style: one short (1 sentence), two medium (2 sentences), one detailed (3 sentences)
- Sound like a real student — not promotional

Reply ONLY with a JSON array of 4 strings. No markdown, no explanation.
Example: ["Great course!", "Really enjoyed the content.", "The instructor explains concepts clearly and the projects were practical.", "I learned a lot from this course. The topics were well structured and easy to follow. Highly recommend to anyone starting out."]`;

    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        signal: abortControllerRef.current.signal,
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model: "claude-3-5-sonnet-20241022",
          max_tokens: 400,
          messages: [{ role: "user", content: prompt }],
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error?.message || `API error ${res.status}`);
      }

      const data = await res.json();
      const raw = (data.content || []).map((b) => b.text || "").join("");
      const clean = raw.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      if (Array.isArray(parsed)) setSuggestions(parsed.slice(0, 4));
    } catch (e) {
      if (e.name !== "AbortError") {
        setSuggestionsError(e.message || "Could not load suggestions");
      }
    } finally {
      setLoadingSuggestions(false);
    }
  };

  useEffect(() => {
    if (!showWriteModal) {
      setSuggestions([]);
      setSuggestionsError("");
      return;
    }
    if (newRating === 0) return;
    
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newRating, newComment);
    }, 600);

    return () => {
      clearTimeout(debounceRef.current);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [newRating, newComment, showWriteModal, apiKey]);

  const useSuggestion = (text) => {
    setNewComment((prev) => (prev ? prev.trim() + " " + text : text));
    setSuggestions([]);
  };
  // -------------------------------------

  useEffect(() => {
    const token = localStorage.getItem("token");
    // if (!token) {
    //   navigate("/login", { state: { from: location.pathname, course } });
    //   return;
    // }

    if (!course && slug) {
      // If we don't have course state, redirect to the course page so it loads properly
      navigate(`/${slug}`);
    }
  }, [course, slug, navigate, location.pathname]);

  useEffect(() => {
    if (location.state?.openWriteModal && localStorage.getItem("token")) {
      setShowWriteModal(true);
    }
  }, [location.state]);

  useEffect(() => {
    fetchGoogleReviews();
  }, []);

  useEffect(() => {
    if (!courseId && !slug) {
      const fetchAllReviews = async () => {
        try {
          setLoadingReviews(true);
          const res = await axiosInstance.get("/testimonials");
          if (res.data && !res.data.error) {
            const approved = (res.data.data || []).filter(
              (t) => t.status === 1 || t.status === "1"
            );
            setDbReviews(approved);
          }
        } catch (err) {
          console.error("Failed to fetch all testimonials:", err);
        } finally {
          setLoadingReviews(false);
        }
      };

      const fetchCoursesList = async () => {
        try {
          const res = await axiosInstance.get("/course");
          if (res.data && !res.data.error) {
            setCoursesList(res.data.data?.data || res.data.data || []);
          }
        } catch (err) {
          console.error("Failed to fetch courses list:", err);
        }
      };

      fetchAllReviews();
      fetchCoursesList();
    }
  }, [courseId, slug]);

  useEffect(() => {
    const fetchTestimonialReviews = async () => {
      try {
        const res = await axiosInstance.get("/testimonials");
        if (res.data && !res.data.error) {
          // Filter testimonials where company/designation matches courseName, and status is 1 (published)
          const filtered = (res.data.data || []).filter(
            (t) =>
              (t.status === 1 || t.status === "1") &&
              (t.company?.toLowerCase().trim() === courseName?.toLowerCase().trim() ||
                t.designation?.toLowerCase().trim() === courseName?.toLowerCase().trim())
          );
          setTestimonialReviews(filtered);
        }
      } catch (err) {
        console.error("Error fetching testimonials in Reviews.js:", err);
      }
    };
    if (courseName) {
      fetchTestimonialReviews();
    }
  }, [courseName]);

  useEffect(() => {
    if (!propReviews && course?.ID) {
      const fetchReviewsData = async () => {
        try {
          setLoadingReviews(true);
          const res = await axiosInstance.get(`course/${course.ID}`);
          if (res.data && !res.data.error) {
            setDbReviews(res.data.data.reviews || []);
            setUserReview(res.data.data.userReview || null);
            setIsPurchased(!!res.data.data.isPurchased);
          }
        } catch (err) {
          console.error("Failed to fetch course reviews:", err);
        } finally {
          setLoadingReviews(false);
        }
      };
      fetchReviewsData();
    }
  }, [course?.ID, propReviews]);

  useEffect(() => {
    if (selectedCourseId) {
      const checkPurchase = async () => {
        try {
          setCheckingPurchase(true);
          const res = await axiosInstance.get(`course/${selectedCourseId}`);
          if (res.data && !res.data.error) {
            setIsCoursePurchased(!!res.data.data.isPurchased);
          }
        } catch (err) {
          console.error("Error checking course purchase status:", err);
          setIsCoursePurchased(false);
        } finally {
          setCheckingPurchase(false);
        }
      };
      checkPurchase();
    } else {
      setIsCoursePurchased(false);
    }
  }, [selectedCourseId]);

  const fetchGoogleReviews = async () => {
    try {
      const res = await axiosInstance.get(`${process.env.REACT_APP_LOGIN_API_BASEURL}/v1/google-review`);
      if (res?.data?.result?.reviews) {
        setGoogleReviews(res.data.result.reviews);
      }
    } catch (error) {
      console.error("Error fetching Google reviews:", error);
    }
  };

  const refreshReviews = async () => {
    const targetCourseId = courseId || selectedCourseId;
    if (targetCourseId) {
      try {
        const resDetails = await axiosInstance.get(`course/${targetCourseId}`);
        if (resDetails.data && !resDetails.data.error) {
          setDbReviews(resDetails.data.data.reviews || []);
          setUserReview(resDetails.data.data.userReview || null);
          setIsPurchased(!!resDetails.data.data.isPurchased);
          setIsCoursePurchased(!!resDetails.data.data.isPurchased);
        }
      } catch (err) {
        console.error("Failed to refresh course reviews:", err);
      }
    } else {
      try {
        const resAll = await axiosInstance.get("/testimonials");
        if (resAll.data && !resAll.data.error) {
          const approved = (resAll.data.data || []).filter(
            (t) => t.status === 1 || t.status === "1"
          );
          setDbReviews(approved);
        }
      } catch (err) {
        console.error("Failed to refresh testimonials:", err);
      }
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) {
      setSubmitErrorMsg("Please enter your review comment.");
      return;
    }
    const targetCourseId = courseId || selectedCourseId;
    if (!targetCourseId) {
      setSubmitErrorMsg("Please select a course to review.");
      return;
    }
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    try {
      setSubmittingReview(true);
      setSubmitErrorMsg("");
      setSubmitSuccessMsg("");

      const res = await axiosInstance.post("course/rate", {
        courseId: targetCourseId,
        rating: newRating,
        comment: newComment
      });

      if (res.data && !res.data.error) {
        setSubmitSuccessMsg("Thank you! Your review has been submitted successfully.");
        setNewComment("");
        setNewRating(5);
        // Refresh local reviews
        await refreshReviews();
        setTimeout(() => {
          setShowWriteModal(false);
          setSubmitSuccessMsg("");
        }, 2000);
      } else {
        setSubmitErrorMsg(res.data?.message || "Failed to submit review.");
      }
    } catch (err) {
      console.error("Error submitting review:", err);
      setSubmitErrorMsg(err.response?.data?.message || "An error occurred. Please try again.");
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleReplySubmit = async (parentId, commentText) => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    try {
      const res = await axiosInstance.post(`/testimonials/${parentId}/reply`, {
        comment: commentText
      });
      if (res.data && !res.data.error) {
        await refreshReviews();
      } else {
        throw new Error(res.data?.message || "Failed to post reply.");
      }
    } catch (err) {
      console.error("Error posting reply:", err);
      throw new Error(err.response?.data?.message || err.message || "An error occurred.");
    }
  };

  // Helper function to flatten the reviews hierarchy
  const flattenReviews = (reviews) => {
    const result = [];
    function recurse(arr) {
      arr.forEach(item => {
        const copy = { ...item };
        const replies = copy.replies;
        delete copy.replies;
        result.push(copy);
        if (replies && replies.length) {
          recurse(replies);
        }
      });
    }
    recurse(reviews);
    return result;
  };

  // Combine API reviews with local high quality reviews
  const allReviews = React.useMemo(() => {
    // Flatten first so we can rebuild the tree uniformly regardless of backend format
    const flatReviews = flattenReviews(dbReviews || []).map(r => {
      const authorName = r.firstName
        ? `${r.firstName} ${r.lastName || ""}`.trim()
        : (r.name || "Student");
      const rawImg = r.profileImage || r.profile;
      const profilePhoto = rawImg
        ? (rawImg.startsWith("http") || rawImg.startsWith("data:") ? rawImg : getS3URL(rawImg))
        : null;
      const commentText = r.comment || r.testimonial || "Highly recommended course.";
      const commentTitle = commentText.split(" ").slice(0, 5).join(" ") + "...";

      return {
        id: r.id,
        parentId: r.parentId,
        author_name: authorName,
        profile_photo_url: profilePhoto,
        rating: r.rating || 5,
        title: commentTitle,
        text: commentText,
        course_name: r.courseName || r.company || r.designation || "",
        relative_time_description: "recently",
        verified: true,
        helpful_count: Number(r.help || 0),
        userRole: r.userRole,
        replies: []
      };
    });

    // Rebuild tree structure
    const map = {};
    flatReviews.forEach(r => {
      map[r.id] = r;
    });

    const roots = [];
    flatReviews.forEach(r => {
      if (r.parentId && map[r.parentId]) {
        if (!map[r.parentId].replies.some(sub => sub.id === r.id)) {
          map[r.parentId].replies.push(r);
        }
      } else {
        roots.push(r);
      }
    });

    return roots;
  }, [dbReviews]);

  const totalReviewsCount = allReviews.length;
  const averageRating = totalReviewsCount > 0
    ? (allReviews.reduce((sum, r) => sum + Number(r.rating || 5), 0) / totalReviewsCount).toFixed(1)
    : "5.0";

  const dynamicStarBreakdown = React.useMemo(() => {
    const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    allReviews.forEach(r => {
      const star = Math.round(Number(r.rating || 5));
      if (counts[star] !== undefined) {
        counts[star]++;
      }
    });
    return [5, 4, 3, 2, 1].map(stars => ({
      stars,
      percentage: totalReviewsCount > 0 ? Math.round((counts[stars] / totalReviewsCount) * 100) : 0
    }));
  }, [allReviews, totalReviewsCount]);

  // Derived filtered & sorted reviews
  const filteredReviews = React.useMemo(() => {
    let list = [...allReviews];

    // Filter by rating star selection
    if (selectedStar !== "all") {
      list = list.filter(r => Math.round(Number(r.rating || 5)) === parseInt(selectedStar));
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(r => 
        r.author_name.toLowerCase().includes(q) || 
        r.text.toLowerCase().includes(q) ||
        (r.course_name && r.course_name.toLowerCase().includes(q))
      );
    }

    // Sort
    list.sort((a, b) => {
      if (sortBy === "helpful") {
        return b.helpful_count - a.helpful_count;
      }
      if (sortBy === "highest") {
        return b.rating - a.rating;
      }
      if (sortBy === "lowest") {
        return a.rating - b.rating;
      }
      if (sortBy === "recent") {
        // Fallback: sort by id desc
        return b.id - a.id;
      }
      return 0;
    });

    return list;
  }, [allReviews, selectedStar, searchQuery, sortBy]);

  const handleHelpfulClick = async (reviewId) => {
    const isVoted = helpfulVotes[reviewId];
    const action = isVoted ? "decrement" : "increment";
    try {
      const res = await axiosInstance.post(`/testimonials/${reviewId}/help`, { action });
      if (res.data && !res.data.error) {
        setHelpfulVotes(prev => ({
          ...prev,
          [reviewId]: !isVoted
        }));
        // Update helper count locally in state
        setDbReviews(prev => {
          function updateHelp(arr) {
            return arr.map(r => {
              if (r.id === reviewId) {
                return { ...r, help: res.data.data.help };
              }
              if (r.replies && r.replies.length) {
                return { ...r, replies: updateHelp(r.replies) };
              }
              return r;
            });
          }
          return updateHelp(prev);
        });
      }
    } catch (err) {
      console.error("Failed to update helpful count:", err);
    }
  };

  if (loadingReviews) {
    return (
      <div className="min-h-[400px] flex flex-col items-center justify-center gap-4 py-20 bg-slate-50/40">
        <div className="h-10 w-10 rounded-full border-4 border-slate-100 border-t-indigo-650 animate-spin" />
        <p className="text-sm font-bold text-slate-500">Loading reviews...</p>
      </div>
    );
  }

  const isAdmin = user?.role === 1 || user?.role === "1";
  const allowedToReview = isAdmin || (courseId ? isPurchased : isCoursePurchased);

  return (
    <div id="reviews-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      
      {/* ── Page Header ── */}
      <div className="mb-12 space-y-2 mt-14">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-black uppercase tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full shadow-3xs">
            <CheckCircle2 className="w-3 h-3 text-indigo-600" />
            Verified Reviews
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full shadow-3xs">
            <Sparkles className="w-3 h-3 text-emerald-600" />
            ReviewCraft AI Enabled
          </span>
        </div>
        <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight leading-none">
          {courseName ? `${courseName} - Student Reviews` : "Student Reviews and Ratings"}
        </h2>
        {/* <p className="text-xs sm:text-sm text-slate-500 font-medium max-w-2xl">
          Hear directly from our student community about their learning paths, projects, and career transformations.
        </p> */}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        
        {/* ── Left column: Summary Stats ── */}
        <div className="lg:col-span-4 bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-6 lg:sticky lg:top-24">
          <div>
            <h3 className="text-lg font-black text-slate-800 tracking-tight">Student Feedback</h3>
            <div className="flex items-baseline gap-2 mt-2">
              <span className="text-5xl font-black text-slate-900 tracking-tight">{averageRating}</span>
              <span className="text-sm font-bold text-slate-400">/ 5</span>
            </div>
            
            <div className="flex items-center gap-2 mt-2">
              <div className="flex text-amber-400">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${
                      i < Math.round(Number(averageRating))
                        ? "fill-amber-400 text-amber-400"
                        : "text-slate-200"
                    }`}
                  />
                ))}
              </div>
              <p className="text-xs text-slate-400 font-bold tracking-wide">
                ({totalReviewsCount} {totalReviewsCount === 1 ? "rating" : "ratings"})
              </p>
            </div>
          </div>

          {/* Progress Bars */}
          <div className="space-y-2.5 pt-2">
            {dynamicStarBreakdown.map((item) => (
              <button
                key={item.stars}
                onClick={() => setSelectedStar(selectedStar === String(item.stars) ? "all" : String(item.stars))}
                className={`w-full flex items-center gap-3 text-xs hover:opacity-90 transition-all text-left p-2 rounded-xl border ${
                  selectedStar === String(item.stars) 
                    ? 'bg-indigo-50/80 border-indigo-200 text-indigo-700 font-bold shadow-2xs' 
                    : 'bg-white border-transparent text-slate-600 hover:bg-slate-50 font-semibold'
                }`}
              >
                <span className="w-10 whitespace-nowrap flex items-center gap-1 font-bold">
                  {item.stars} <Star className="w-3 h-3 fill-amber-400 text-amber-400 inline-block" />
                </span>
                <div className="flex-1 bg-slate-100 h-2 rounded-full overflow-hidden relative">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      selectedStar === String(item.stars) ? 'bg-indigo-600' : 'bg-gradient-to-r from-amber-400 to-orange-400'
                    }`}
                    style={{ width: `${item.percentage}%` }}
                  />
                </div>
                <span className="w-8 text-right text-slate-400 font-bold text-[10px]">{item.percentage}%</span>
              </button>
            ))}
          </div>

          {((courseId ? isPurchased : true) || isAdmin) && (
            <div className="border-t border-slate-100 pt-6 bg-gradient-to-br from-indigo-50/20 via-transparent to-transparent -mx-6 px-6 -mb-6 pb-6 rounded-b-2xl">
              <h4 className="font-bold text-slate-800 text-sm mb-1 flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                Review this program
              </h4>
              <p className="text-[11px] text-slate-400 leading-relaxed mb-4 font-semibold">
                Share your thoughts with other learners to help them find the right program.
              </p>
              <button
                onClick={() => {
                  if (!localStorage.getItem("token")) {
                    navigate("/login");
                  } else {
                    setShowWriteModal(true);
                  }
                }}
                className="w-full bg-slate-900 hover:bg-black text-white py-3 px-4 rounded-xl text-xs font-bold shadow-sm hover:shadow-md hover:shadow-indigo-500/5 active:scale-98 transition-all flex items-center justify-center gap-2 cursor-pointer border border-transparent"
              >
                <MessageSquare className="w-4 h-4 text-white/80" />
                {userReview ? "Edit your review" : "Write a review"}
              </button>
            </div>
          )}
        </div>

        {/* ── Right column: Review List ── */}
        <div className="lg:col-span-8 space-y-6">
          
          {/* Filters strip */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 bg-slate-50/50 p-4 rounded-2xl border border-slate-100 shadow-3xs">
            <div className="relative flex-1 min-w-[240px]">
              <input
                type="text"
                placeholder="Search student reviews..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white border border-slate-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 focus:bg-white rounded-xl pl-9 pr-8 py-2 text-xs font-bold text-slate-700 placeholder-slate-400 outline-none transition-all shadow-3xs"
              />
              {/* <Search className="w-3.5 h-3.5 text-slate-400  left-3 top-1/2 -translate-y-1/2" />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-0.5"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )} */}
            </div>

            <div className="flex flex-wrap items-center gap-3 self-end sm:self-auto">
              {selectedStar !== "all" && (
                <button
                  onClick={() => setSelectedStar("all")}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100/70 border border-indigo-150 rounded-lg transition-all cursor-pointer shadow-3xs"
                >
                  Clear Rating ({selectedStar}★)
                  <X className="w-2.5 h-2.5" />
                </button>
              )}

              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Sort By</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-bold text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-100 cursor-pointer hover:border-slate-350 transition-all shadow-3xs"
                >
                  <option value="helpful">Most Helpful</option>
                  <option value="highest">Highest Rating</option>
                  <option value="lowest">Lowest Rating</option>
                  <option value="recent">Most Recent</option>
                </select>
              </div>
            </div>
          </div>

          {/* Search Result Information */}
          {(searchQuery || selectedStar !== "all") && (
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider pl-1">
              Showing {filteredReviews.length} of {totalReviewsCount} reviews matching your filters
            </div>
          )}

          {/* Review items */}
          <div className="space-y-6">
            {filteredReviews.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-slate-100 shadow-sm space-y-4">
                <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center mx-auto border border-slate-100">
                  <Search className="w-5 h-5 text-slate-400" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-base font-bold text-slate-800">No reviews match your filters</h3>
                  <p className="text-xs text-slate-400 max-w-xs mx-auto">Try typing a different keyword, choosing another star rating, or resetting filters.</p>
                </div>
                <button
                  onClick={() => { setSelectedStar("all"); setSearchQuery(""); }}
                  className="px-4 py-2 bg-slate-900 hover:bg-black text-white rounded-xl text-xs font-bold hover:shadow-lg hover:shadow-slate-500/10 active:scale-95 transition-all cursor-pointer"
                >
                  Reset All Filters
                </button>
              </div>
            ) : (
              filteredReviews.map((review, idx) => (
                <ReviewNode
                  key={review.id || idx}
                  review={review}
                  onReplySubmit={handleReplySubmit}
                  handleHelpfulClick={handleHelpfulClick}
                  helpfulVotes={helpfulVotes}
                  level={0}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── Write Review Modal ── */}
      {showWriteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md animate-fadeIn mt-16"
          onClick={(e) => e.target === e.currentTarget && setShowWriteModal(false)}
        >
          <form
            onSubmit={handleReviewSubmit}
            onClick={(e) => e.stopPropagation()}
            className={`relative w-full mx-4 bg-white rounded-3xl shadow-2xl overflow-hidden animate-scaleUp transition-all duration-300 ${
              newRating > 0 && showSuggestionsList ? "max-w-4xl" : "max-w-md"
            }`}
          >
            {/* Top Accent Gradient Bar */}
            <div className="h-1.5 w-full bg-gradient-to-r from-violet-500 via-indigo-500 to-purple-600" />

            {/* Modal Header */}
            <div className="flex justify-between items-center px-6 pt-5 pb-4 border-b border-slate-100 bg-white">
              <div>
                <h3 className="text-lg font-black text-slate-800">{userReview ? "Edit your Review" : "Write a Review"}</h3>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-1">{activeCourseName || "Program Review"}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowWriteModal(false)}
                className="text-slate-400 hover:text-slate-700 bg-slate-50 hover:bg-slate-100 p-2 rounded-full border border-slate-100 hover:border-slate-200 transition-all cursor-pointer flex items-center justify-center"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Modal Body with 2-Column Split when Suggestions are shown */}
            <div className={`grid grid-cols-1 ${newRating > 0 && showSuggestionsList ? "lg:grid-cols-12" : ""}`}>
              {/* Left Column: Form Content */}
              <div className={`px-6 py-5 space-y-5 ${
                newRating > 0 && showSuggestionsList ? "lg:col-span-7" : ""
              }`}>
                {showSettings && (
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-3 animate-fadeIn">
                    <div className="flex items-center gap-2 text-slate-800 font-black text-sm">
                      <Key className="w-4 h-4 text-indigo-650" />
                      <span>API Provider Configuration</span>
                    </div>
                    <p className="text-[11px] text-slate-500 leading-relaxed font-semibold">
                      To generate real-time AI suggestions, input your Anthropic API Key below. If left empty, the portal will automatically use the built-in Free Local Suggester at zero cost.
                    </p>
                    
                    {process.env.REACT_APP_ANTHROPIC_KEY && (
                      <div className="flex items-center gap-1.5 text-[10px] text-emerald-600 font-bold bg-emerald-50 border border-emerald-100 px-2 py-1 rounded-lg">
                        <Check className="w-3.5 h-3.5" />
                        <span>Loaded default key from environment.</span>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input
                          type={showKey ? "text" : "password"}
                          value={apiKeyInput}
                          onChange={(e) => setApiKeyInput(e.target.value)}
                          placeholder="sk-ant-..."
                          className="w-full bg-white border border-slate-200 rounded-xl pl-3 pr-8 py-2 text-xs font-semibold text-slate-700 outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 transition-all"
                        />
                        <button
                          type="button"
                          onClick={() => setShowKey(!showKey)}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-0.5"
                        >
                          {showKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const trimmed = apiKeyInput.trim();
                          setApiKey(trimmed);
                          localStorage.setItem("anthropic_api_key", trimmed);
                          setKeySaved(true);
                          setTimeout(() => setKeySaved(false), 2000);
                        }}
                        className={`px-3 py-2 text-xs font-bold rounded-xl text-white shadow-3xs hover:-translate-y-px active:scale-95 transition-all flex items-center gap-1 cursor-pointer ${
                          keySaved ? "bg-emerald-605 hover:bg-emerald-700" : "bg-indigo-600 hover:bg-indigo-700"
                        }`}
                      >
                        {keySaved ? <Check className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />}
                        <span>{keySaved ? "Saved" : "Save"}</span>
                      </button>
                    </div>
                    
                    <div className="flex items-start gap-1.5 text-[9px] text-amber-600 leading-normal font-semibold bg-amber-50/50 border border-amber-100/50 p-2 rounded-lg">
                      <ShieldAlert className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <span>Your key is stored in your browser's local state and sent directly to Anthropic's official API. It is never sent to any third-party server.</span>
                    </div>
                  </div>
                )}
                {submitErrorMsg && (
                  <div className="p-3 bg-rose-50 border border-rose-100 text-rose-800 text-xs font-bold rounded-xl">
                    {submitErrorMsg}
                  </div>
                )}
                {submitSuccessMsg && (
                  <div className="p-3 bg-emerald-50 border border-emerald-100 text-emerald-800 text-xs font-bold rounded-xl">
                    {submitSuccessMsg}
                  </div>
                )}

                {!courseId && (
                  <div>
                    <label className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-2">
                      Select Course <span className="text-rose-500">*</span>
                    </label>
                    <select
                      value={selectedCourseId}
                      onChange={(e) => setSelectedCourseId(e.target.value)}
                      className="w-full bg-slate-55 border border-slate-205 rounded-xl px-4 py-3 text-sm font-semibold text-slate-700 focus:outline-none focus:ring-4 focus:ring-slate-100 focus:bg-white transition-all cursor-pointer shadow-3xs"
                      required
                    >
                      <option value="">-- Choose a Course --</option>
                      {coursesList.map((c) => (
                        <option key={c.ID} value={c.ID}>
                          {c.CourseName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {selectedCourseId && checkingPurchase ? (
                  <div className="flex items-center gap-2 text-xs font-bold text-slate-500 py-4 justify-center">
                    <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
                    Checking course enrollment...
                  </div>
                ) : (selectedCourseId || courseId) && !allowedToReview ? (
                  <div className="p-4 bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold rounded-2xl flex items-start gap-2.5">
                    <ShieldAlert className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span>You can only review courses you have purchased and enrolled in.</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Rating Star Selection */}
                    <div>
                      <label className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-2">
                        Select Rating <span className="text-rose-500">*</span>
                      </label>
                      <div className="flex items-center gap-2 bg-slate-50/50 p-4 rounded-2xl border border-slate-100">
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onMouseEnter={() => setHoveredRating(star)}
                              onMouseLeave={() => setHoveredRating(0)}
                              onClick={() => setNewRating(star)}
                              className="text-2xl transition-all duration-200 hover:scale-120 focus:outline-none cursor-pointer flex items-center justify-center active:scale-95"
                            >
                              <Star
                                className={`w-6 h-6 transition-all duration-150 ${
                                  star <= (hoveredRating || newRating)
                                    ? "fill-amber-400 text-amber-400 drop-shadow-star"
                                    : "text-slate-200"
                                }`}
                              />
                            </button>
                          ))}
                        </div>
                        {(() => {
                          const currentVal = hoveredRating || newRating;
                          const badges = {
                            5: { text: "Excellent, loved it!", classes: "text-emerald-700 bg-emerald-50 border-emerald-100" },
                            4: { text: "Very Good, highly recommended", classes: "text-blue-700 bg-blue-50 border-blue-100" },
                            3: { text: "Good, standard experience", classes: "text-amber-700 bg-amber-50 border-amber-100" },
                            2: { text: "Fair, could be improved", classes: "text-orange-700 bg-orange-50 border-orange-100" },
                            1: { text: "Poor, did not meet expectations", classes: "text-rose-700 bg-rose-50 border-rose-100" },
                          };
                          const activeBadge = badges[currentVal] || { text: "Select Rating", classes: "text-slate-400 bg-slate-50 border-slate-150" };
                          return (
                            <span className={`ml-auto px-3 py-1.5 text-[10px] font-black uppercase tracking-wider border rounded-xl shadow-3xs transition-all ${activeBadge.classes}`}>
                              {activeBadge.text}
                            </span>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Review Textarea */}
                    <div>
                      <label className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-2">
                        Review Content <span className="text-rose-500">*</span>
                      </label>
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="How was your learning experience? Share your feedback with us..."
                        rows="4"
                        className="w-full bg-slate-50/50 border border-slate-200 rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 focus:outline-none focus:ring-4 focus:ring-slate-100 focus:border-slate-300 focus:bg-white transition-all resize-none shadow-3xs hover:border-slate-300"
                        required
                      />
                    </div>

                    {/* Suggestions Toggle Button */}
                    {newRating > 0 && (
                      <div className="flex items-center justify-between bg-slate-50/50 p-4 rounded-2xl border border-slate-100">
                        <span className="text-xs font-semibold text-slate-505">
                          Need help writing? Use suggestions
                        </span>
                        <button
                          type="button"
                          onClick={() => setShowSuggestionsList(!showSuggestionsList)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all border shadow-3xs cursor-pointer ${
                            showSuggestionsList
                              ? "bg-slate-900 border-slate-900 text-white scale-[1.02] shadow-md"
                              : "bg-white border-slate-200 text-slate-500 hover:border-slate-350 hover:bg-slate-50 hover:text-slate-700 active:scale-95"
                          }`}
                        >
                          <Sparkles className="w-3.5 h-3.5 text-indigo-650" />
                          <span>{showSuggestionsList ? "Hide Suggestions" : "Suggestions"}</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right Column: Suggestions Box */}
              {newRating > 0 && showSuggestionsList && (
                <div className="lg:col-span-5 border-t lg:border-t-0 lg:border-l border-slate-100 flex flex-col bg-slate-50/30">
                  {/* Header/Title for Suggestions Section */}
                  <div className="px-6 pt-5 pb-3 border-b border-slate-100/80 flex items-center justify-between bg-white/50">
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-indigo-650" />
                      <span className="text-[11px] font-black text-slate-800 uppercase tracking-wider">
                        {apiKey ? "AI Suggestions" : "Free Suggestions"}
                      </span>
                    </div>
                    <span
                      className={`text-[8px] font-extrabold uppercase px-1.5 py-0.5 rounded-md border tracking-wide ${
                        apiKey 
                          ? "bg-emerald-50 border-emerald-100 text-emerald-600" 
                          : "bg-indigo-50 border-indigo-100 text-indigo-600"
                      }`}
                    >
                      {apiKey ? "Online" : "Free"}
                    </span>
                  </div>

                  {/* Suggestions Content */}
                  <div className="p-6 space-y-4 max-h-[350px] overflow-y-auto flex-1 scrollbar-thin">
                    <p className="text-[11px] font-semibold text-slate-400 leading-relaxed">
                      {apiKey 
                        ? "Select custom AI suggestions derived from the course topics and rating to expand your feedback." 
                        : "Select from our curated suggestion templates matching your chosen star rating."}
                    </p>

                    {loadingSuggestions ? (
                      <div className="flex flex-col items-center justify-center py-8 gap-2.5">
                        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                        <span className="text-[10px] font-bold text-slate-500">Generating suggestions...</span>
                      </div>
                    ) : suggestionsError ? (
                      <div className="text-xs font-semibold text-red-750 bg-red-50 border border-red-100 p-2.5 rounded-xl flex items-start gap-2">
                        <ShieldAlert className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                        <span>{suggestionsError}</span>
                      </div>
                    ) : suggestions.length > 0 ? (
                      <div className="flex flex-col gap-3">
                        {suggestions.map((sug, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => useSuggestion(sug)}
                            className="w-full text-left text-s font-bold text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-350 p-4 rounded-xl transition-all duration-250 shadow-3xs cursor-pointer hover:-translate-y-0.5 hover:shadow-xs active:translate-y-0 active:scale-[0.99] leading-relaxed flex flex-col gap-1.5"
                          >
                            <span>{sug}</span>
                            <span className="text-[9px] font-black font-bold text-indigo-600 uppercase tracking-wider self-end opacity-60 hover:opacity-100 transition-opacity">
                              + Use template
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-[11px] text-slate-400 font-semibold italic text-center py-6">
                        {newComment.length > 10
                          ? "Keep typing — suggestions will refresh…"
                          : "Suggestions will appear here based on your rating."}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-3 bg-white">
              <button
                type="button"
                onClick={() => setShowWriteModal(false)}
                className="bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 px-5 py-2.5 rounded-xl text-xs font-bold hover:shadow-2xs active:scale-95 transition-all cursor-pointer"
                disabled={submittingReview}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submittingReview || (!courseId && !selectedCourseId) || !allowedToReview}
                className="bg-slate-900 hover:bg-black text-white px-6 py-2.5 rounded-xl text-xs font-bold shadow-md hover:shadow-lg active:scale-95 transition-all flex items-center gap-2 cursor-pointer border border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submittingReview ? "Submitting..." : "Submit Review"}
              </button>
            </div>
          </form>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleUp { from { transform: scale(0.96); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        .animate-fadeIn  { animation: fadeIn  0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-scaleUp { animation: scaleUp 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .scrollbar-thin::-webkit-scrollbar       { width: 6px; }
        .scrollbar-thin::-webkit-scrollbar-track { background: transparent; }
        .scrollbar-thin::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        .drop-shadow-star { filter: drop-shadow(0 0 5px rgba(251, 191, 36, 0.5)); }
      `}</style>
    </div>
  );
}

export default Reviews;
